import ProductSlider from './ProductSlider';
import ProductSliderSkeleton from './ProductSliderSkeleton';
import { mapProductToCard } from './mapProductToCard';
import {
  useGetProductsByCategoryQuery,
  useGetProductsBySubcategoryQuery,
} from '../../../store/slices/storefrontProductsSlice';

interface CategorySliderProps {
  /** The category or subcategory id to fetch products for. */
  id: string;
  /** Whether `id` refers to a category or a subcategory. */
  type: 'category' | 'subcategory';
  title: string;
  /** Optional "View All" destination. */
  viewAllLink?: string;
}

/**
 * Renders a single horizontal product slider for one category or subcategory.
 * Each instance owns its own RTK Query call, which lets the HomePage render an
 * arbitrary number of sliders without breaking the rules of hooks. Both query
 * hooks are always called; the irrelevant one is skipped. Fully client-side:
 * reuses the existing /product/category/:id and /product/subcategory/:id
 * endpoints.
 */
export default function CategorySlider({ id, type, title, viewAllLink }: CategorySliderProps) {
  const isCategory = type === 'category';

  const categoryResult = useGetProductsByCategoryQuery(
    { categoryId: id },
    { skip: !isCategory }
  );
  const subcategoryResult = useGetProductsBySubcategoryQuery(
    { subcategoryId: id },
    { skip: isCategory }
  );

  const { data: products = [], isLoading, error } = isCategory
    ? categoryResult
    : subcategoryResult;

  if (isLoading) {
    return <ProductSliderSkeleton />;
  }

  // Hide the slider entirely on error or when there are no products.
  if (error || products.length === 0) {
    return null;
  }

  return (
    <ProductSlider
      title={title}
      viewAllLink={viewAllLink}
      products={products.map(mapProductToCard)}
    />
  );
}
